import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronUp } from 'react-feather';
import { Badge } from '../ui/badge';
import ClickableFeatureTag from '../shared/ClickableFeatureTag';
import { formatDueDate } from '../../utils/taskUtils';
import './BacklogSection.css';

const BacklogSection = ({ backlog, projectId, onTaskClick, variant, allFeatureTags = [], allTasks = [] }) => {
    const isReport = variant === 'report';
    const [isCollapsed, setIsCollapsed] = useState(true);
    const [groupByFeature, setGroupByFeature] = useState(false);

    const getDaysSinceCreation = (createdAt) => {
        if (!createdAt) return 0;
        const created = new Date(createdAt);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        created.setHours(0, 0, 0, 0);
        const diffTime = today - created;
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    const formatDays = (days) => {
        if (days === 0) return 'Today';
        if (days === 1) return '1 day ago';
        return `${days} days ago`;
    };

    const getDaysColor = (days) => {
        if (days > 7) return 'red';
        if (days > 3) return 'yellow';
        return 'green';
    };

    // Group tasks by feature tag
    const tasksByFeature = useMemo(() => {
        if (!groupByFeature || !backlog) return null;

        const featureMap = new Map();

        backlog.forEach(task => {
            const featureId = task.feature_tag?.id || 'untagged';
            const featureName = task.feature_tag?.name || 'Untagged';
            const featureColor = task.feature_tag?.color || '#6b7280';

            if (!featureMap.has(featureId)) {
                featureMap.set(featureId, {
                    id: featureId,
                    name: featureName,
                    color: featureColor,
                    dueDate: task.feature_tag?.due_date,
                    tasks: []
                });
            }

            featureMap.get(featureId).tasks.push(task);
        });

        // Sort features by name (untagged last)
        const features = Array.from(featureMap.values());
        features.sort((a, b) => {
            if (a.id === 'untagged') return 1;
            if (b.id === 'untagged') return -1;
            return a.name.localeCompare(b.name);
        });

        // Sort tasks within each feature by creation date (oldest first)
        features.forEach(feature => {
            feature.tasks.sort((a, b) => {
                const dateA = new Date(a.created_at);
                const dateB = new Date(b.created_at);
                return dateA - dateB;
            });
        });

        return features;
    }, [backlog, groupByFeature]);

    if (!backlog || backlog.length === 0) {
        return (
            <div className="backlog-section">
                <div 
                    className="section-header" 
                    onClick={() => setIsCollapsed(!isCollapsed)} 
                    style={{ cursor: 'pointer' }}
                >
                    <h2>Backlog (Unassigned Tasks)</h2>
                    <button 
                        className="stats-toggle-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsCollapsed(!isCollapsed);
                        }}
                        aria-label={isCollapsed ? "Expand" : "Collapse"}
                    >
                        {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                    </button>
                </div>
                <div className={`section-content ${isCollapsed ? 'collapsed' : ''}`}>
                    <div className="empty-state success">
                        <p>✅ No unassigned tasks in backlog.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`backlog-section ${isReport ? 'backlog-section-report' : ''}`}>
            <div 
                className="section-header" 
                onClick={() => setIsCollapsed(!isCollapsed)} 
                style={{ cursor: 'pointer' }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <h2>Backlog (Unassigned Tasks)</h2>
                    <Badge variant="warning">
                        {backlog.length} {backlog.length === 1 ? 'task' : 'tasks'} unassigned
                    </Badge>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    {!isReport && !isCollapsed && (
                        <label 
                            className="group-by-feature-toggle"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <span className="toggle-label">Group by Feature</span>
                            <div 
                                className={`toggle-switch ${groupByFeature ? 'active' : ''}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setGroupByFeature(!groupByFeature);
                                }}
                            >
                                <div className="toggle-slider"></div>
                            </div>
                        </label>
                    )}
                    <button 
                        className="stats-toggle-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsCollapsed(!isCollapsed);
                        }}
                        aria-label={isCollapsed ? "Expand" : "Collapse"}
                    >
                        {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                    </button>
                </div>
            </div>
            <div className={`section-content ${isCollapsed ? 'collapsed' : ''}`}>
                {groupByFeature && tasksByFeature ? (
                    <div className="grouped-by-feature-container">
                        {tasksByFeature.map((feature) => (
                            <div key={feature.id} className="feature-group">
                                <div className="feature-group-header">
                                    <div className="feature-group-info">
                                        <ClickableFeatureTag
                                            featureTag={feature}
                                            allFeatureTags={allFeatureTags}
                                            allTasks={allTasks}
                                            size="small"
                                        />
                                        <span className="feature-group-meta">
                                            {feature.tasks.length} {feature.tasks.length === 1 ? 'task' : 'tasks'}
                                        </span>
                                    </div>
                                </div>
                                <div className="table-container">
                                    <table className="backlog-table">
                                        <thead>
                                            <tr>
                                                <th>Task</th>
                                                <th>Status</th>
                                                <th>Days Since Creation</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {feature.tasks.map((task) => {
                                                const daysSince = getDaysSinceCreation(task.created_at);
                                                return (
                                                    <tr
                                                        key={task.id}
                                                        className={`backlog-task-row ${!isReport && onTaskClick ? 'backlog-task-row-clickable' : ''}`}
                                                        onClick={!isReport && onTaskClick ? () => onTaskClick({
                                                            id: task.id,
                                                            title: task.title,
                                                            display_id: task.display_id,
                                                            description: task.description,
                                                            status: task.status,
                                                            feature_tag: task.feature_tag,
                                                            created_at: task.created_at,
                                                            updated_at: task.updated_at
                                                        }) : undefined}
                                                    >
                                                        <td>
                                                            <div className="task-info">
                                                                <div className="task-title-row">
                                                                    {task.display_id && (
                                                                        <span className="task-id-bracket">[{task.display_id}]</span>
                                                                    )}
                                                                    <div className="task-title">{task.title}</div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <Badge
                                                                variant={task.status === 'in-progress' ? 'warning' : 'secondary'}
                                                            >
                                                                {task.status}
                                                            </Badge>
                                                        </td>
                                                        <td>
                                                            <span className={`days-since ${getDaysColor(daysSince)}`}>
                                                                {formatDays(daysSince)}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="table-container">
                        <table className="backlog-table">
                            <thead>
                                <tr>
                                    <th>Task</th>
                                    <th>Status</th>
                                    <th>Days Since Creation</th>
                                </tr>
                            </thead>
                            <tbody>
                                {backlog.map((task) => {
                                    const daysSince = getDaysSinceCreation(task.created_at);
                                    return (
                                        <tr
                                            key={task.id}
                                            className={`backlog-task-row ${!isReport && onTaskClick ? 'backlog-task-row-clickable' : ''}`}
                                            onClick={!isReport && onTaskClick ? () => onTaskClick({
                                                id: task.id,
                                                title: task.title,
                                                display_id: task.display_id,
                                                description: task.description,
                                                status: task.status,
                                                feature_tag: task.feature_tag,
                                                created_at: task.created_at,
                                                updated_at: task.updated_at
                                            }) : undefined}
                                        >
                                            <td>
                                                <div className="task-info">
                                                    <div className="task-title-row">
                                                        {task.display_id && (
                                                            <span className="task-id-bracket">[{task.display_id}]</span>
                                                        )}
                                                        <div className="task-title">{task.title}</div>
                                                        {task.feature_tag && (() => {
                                                            const dueDateInfo = task.feature_tag.due_date 
                                                                ? formatDueDate(task.feature_tag.due_date) 
                                                                : null;
                                                            const tagName = dueDateInfo 
                                                                ? `${task.feature_tag.name} (${dueDateInfo.text})`
                                                                : task.feature_tag.name;
                                                            return (
                                                                <ClickableFeatureTag
                                                                    featureTag={task.feature_tag}
                                                                    allFeatureTags={allFeatureTags}
                                                                    allTasks={allTasks}
                                                                    name={tagName}
                                                                    size="small"
                                                                />
                                                            );
                                                        })()}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <Badge
                                                    variant={task.status === 'in-progress' ? 'warning' : 'secondary'}
                                                >
                                                    {task.status}
                                                </Badge>
                                            </td>
                                            <td>
                                                <span className={`days-since ${getDaysColor(daysSince)}`}>
                                                    {formatDays(daysSince)}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BacklogSection;

